import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { assignDeviceMutation } from '../../lib/rescueQueries';
import styles from './AssignDeviceToPetModal.module.scss';

interface PetInfo {
  id: string;
  name: string;
  breed?: { name: string } | null;
  chip?: { serialNumber: string } | null;
}

interface AssignDeviceToPetModalProps {
  open: boolean;
  onClose: () => void;
  pet: PetInfo | null;
}

type AssignMode = 'serial' | 'stock';

export default function AssignDeviceToPetModal({ open, onClose, pet }: AssignDeviceToPetModalProps) {
  const { showToast } = useToast();
  const [mode, setMode] = useState<AssignMode>('serial');
  const [serialNumber, setSerialNumber] = useState('');
  const [serialError, setSerialError] = useState('');

  const [assignDevice, { loading: assigning }] = useMutation(assignDeviceMutation, {
    refetchQueries: 'active' as const,
  });

  function handleClose() {
    setMode('serial');
    setSerialNumber('');
    setSerialError('');
    onClose();
  }

  async function handleAssign() {
    if (!pet) {
      return;
    }
    const moduleId = serialNumber.trim().toUpperCase();
    if (!moduleId) {
      setSerialError('Serial number is required');
      return;
    }
    try {
      await assignDevice({ variables: { moduleId, petId: pet.id } });
      showToast(`Device ${moduleId} assigned to ${pet.name}`);
      handleClose();
    } catch (err) {
      setSerialError(err instanceof Error ? err.message : 'Failed to assign device');
    }
  }

  if (!pet) {
    return null;
  }

  const subtitle = [pet.breed?.name, pet.chip ? `Chip ${pet.chip.serialNumber}` : null]
    .filter(Boolean)
    .join(' \u00B7 ');
  const canSubmit = serialNumber.trim().length > 0 && !assigning;

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Add Device to {pet.name}</ModalTitle>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

      <div className={styles.modeLabel}>Two ways to add a device &mdash; enter serial or pick from stock</div>

      <div className={styles.radioCards}>
        <button
          type="button"
          className={`${styles.radioCard} ${mode === 'serial' ? styles.radioCardSelected : ''}`}
          onClick={() => setMode('serial')}
        >
          <span className={`${styles.radioDot} ${mode === 'serial' ? styles.radioDotSelected : ''}`} />
          <div>
            <div className={styles.radioLabel}>Enter device serial number</div>
            <div className={styles.radioDesc}>Enter a serial number to assign a new device</div>
          </div>
        </button>
        <button
          type="button"
          className={`${styles.radioCard} ${mode === 'stock' ? styles.radioCardSelected : ''}`}
          onClick={() => setMode('stock')}
          disabled
          style={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          <span className={`${styles.radioDot} ${mode === 'stock' ? styles.radioDotSelected : ''}`} />
          <div>
            <div className={styles.radioLabel}>Select from unassigned devices</div>
            <div className={styles.radioDesc}>Coming soon</div>
          </div>
        </button>
      </div>

      <PortalField label="Serial Number" error={serialError} showError={!!serialError}>
        <PortalInput
          value={serialNumber}
          onChange={(e) => {
            setSerialNumber(e.target.value);
            setSerialError('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleAssign()}
          placeholder="e.g. M1XXXXXXXX"
          hasError={!!serialError}
          autoFocus
        />
      </PortalField>

      <div className={styles.infoBanner}>
        If this device hasn&rsquo;t been activated yet, plug it in via USB-C after assigning. Battery and location data
        will appear once the device powers on.
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleAssign} disabled={!canSubmit}>
          {assigning ? 'Assigning...' : `Assign to ${pet.name}`}
        </Button>
      </ModalActions>
    </Modal>
  );
}
